import React from 'react';

import { DynamicEnergyIcon } from './DynamicEnergyIcon';

const DEFAULT_RANGE = 'AG';

type EnergyRatingProps = {
    letter?: string;
};

/**
 * EnergyRating Component
 *
 * A dynamic energy rating component that processes input and renders energy efficiency labels.
 * Supports multiple energy rating ranges (A-F, A-G, etc.) with dynamic component loading.
 *
 * @param props.letter - Energy rating input string that can be:
 *   - 1 character: "A" (uses default AG range) - case insensitive
 *   - 3 characters: "AFC" (AF range with C letter) - case insensitive
 *   - Special case: "AFAPlus" (AF range with APlus letter) - case insensitive
 *   - No letter or invalid inputs return null
 *
 * Processing Logic:
 * 1. Input validation: Returns null if no letter provided
 * 2. Input processing: Trims whitespace, converts to uppercase (case insensitive)
 * 3. Special case handling: "AFAPlus" (any case) gets special treatment
 * 4. Format detection:
 *    - 3 chars: Parses as range (first 2) + letter (last 1)
 *    - 1 char: Uses DEFAULT_RANGE with provided letter
 *    - 2 chars or invalid: Returns null
 * 5. Component rendering: Passes to DynamicEnergyIcon for validation and dynamic loading
 *
 * @returns React component or null if validation fails or component doesn't exist
 */
export function EnergyRating(props: EnergyRatingProps) {
    if (!props.letter) {
        return null;
    }

    const cleanInput = props.letter.replace(/\s+/g, '').toUpperCase();

    // Special case: Handle AFAPlus
    if (cleanInput === 'AFAPLUS') {
        return <DynamicEnergyIcon range="AF" letter="APlus" defaultRange={DEFAULT_RANGE} />;
    }

    const input = cleanInput.substring(0, 3);

    switch (input.length) {
        case 3: {
            const scale = input.substring(0, 2);
            const ratingLetter = input.substring(2, 3);
            return <DynamicEnergyIcon range={scale} letter={ratingLetter} defaultRange={DEFAULT_RANGE} />;
        }
        case 1: {
            const ratingLetter = input;
            return <DynamicEnergyIcon range={DEFAULT_RANGE} letter={ratingLetter} defaultRange={DEFAULT_RANGE} />;
        }
        case 2:
        default:
            return null;
    }
}
